// Spotter — product page behaviour: gallery (scroll-snap), variant / selling-plan
// choice, live price on the button, and the mobile sticky buy bar.

const S = window.Spotter || {};

function money(cents) {
  const format = S.moneyFormat || '${{amount}}';
  const value = Number(cents) / 100;
  const fmt = (n, decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = n.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  // Match Liquid's money_without_trailing_zeros: $18 not $18.00.
  const whole = Number.isInteger(value);
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return fmt(value, 0);
      case 'amount_with_comma_separator': return fmt(value, whole ? 0 : 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      default: return fmt(value, whole ? 0 : 2);
    }
  });
}

/* ---------- Gallery ---------- */

class PdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.track.children];
    if (this.slides.length < 2) return;
    this.current = this.querySelector('[data-current]');
    this.dots = [...this.querySelectorAll('.pdp-gallery__dot')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.index = 0;

    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
    });

    // The slide that is mostly in view is the current one.
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.setIndex(this.slides.indexOf(entry.target));
      });
    }, { root: this.track, threshold: 0.6 });
    this.slides.forEach((s) => this.io.observe(s));
    this.update();
  }
  go(i, smooth = true) {
    const n = this.slides.length;
    const target = Math.max(0, Math.min(n - 1, i));
    const slide = this.slides[target];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: smooth ? undefined : 'instant' });
    this.setIndex(target);
  }
  goToMedia(mediaId, smooth = true) {
    const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (i > -1) this.go(i, smooth);
  }
  setIndex(i) {
    if (i < 0 || i === this.index) return;
    this.index = i;
    this.update();
  }
  update() {
    const i = this.index;
    if (this.current) this.current.textContent = i + 1;
    this.dots.forEach((d, n) => d.toggleAttribute('data-active', n === i));
    this.thumbs.forEach((t, n) => {
      if (n === i) {
        t.setAttribute('aria-current', 'true');
        t.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      } else {
        t.removeAttribute('aria-current');
      }
    });
    if (this.prev) this.prev.disabled = i === 0;
    if (this.next) this.next.disabled = i === this.slides.length - 1;
  }
}
customElements.define('pdp-gallery', PdpGallery);

/* ---------- Buy box ---------- */

class PdpMain extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    if (!json) return;
    this.data = JSON.parse(json.textContent);
    this.form = document.getElementById(this.dataset.formId);
    this.variantInput = this.querySelector('[data-variant-input]');
    this.planInput = this.querySelector('[data-plan-input]');
    this.planSelect = this.querySelector('[data-plan-select]');
    this.qtyInput = this.querySelector('[data-qty]');
    this.submit = this.querySelector('[data-submit]');
    this.sticky = this.querySelector('[data-sticky]');
    this.stickyBtn = this.sticky?.querySelector('button');
    this.gallery = this.querySelector('pdp-gallery');

    this.variantId = Number(this.variantInput.value);
    this.planId = this.planInput && !this.planInput.disabled ? this.planInput.value : null;
    this.lastPlanId = this.planSelect?.value || this.planInput?.dataset.defaultPlan || null;

    this.addEventListener('change', (e) => this.onChange(e));
    this.qtyInput?.addEventListener('input', () => this.render());

    // If the URL picked a variant with its own photo, show that photo first.
    const v = this.variant;
    const firstSlide = this.gallery?.querySelector('[data-media-id]');
    if (v?.mediaId && firstSlide && firstSlide.dataset.mediaId !== String(v.mediaId)) {
      requestAnimationFrame(() => this.gallery.goToMedia?.(v.mediaId, false));
    }

    this.initSticky();
    this.render();
  }

  get variant() { return this.data.variants.find((v) => v.id === this.variantId); }

  onChange(e) {
    const t = e.target;
    if (t.matches('[data-variant-radio]')) {
      this.variantId = Number(t.value);
      const v = this.variant;
      if (v?.mediaId) this.gallery?.goToMedia?.(v.mediaId);
      this.updateUrl();
      this.render(true);
    } else if (t.matches('[data-purchase-radio]')) {
      this.planId = t.value === 'subscribe' ? (this.planSelect?.value || this.lastPlanId) : null;
      this.render(true);
    } else if (t.matches('[data-plan-select]')) {
      this.lastPlanId = t.value;
      this.planId = t.value;
      const sub = this.querySelector('[data-purchase-radio][value="subscribe"]');
      if (sub) sub.checked = true;
      this.render(true);
    } else if (t.matches('[data-qty]')) {
      this.render();
    }
  }

  updateUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.variantId);
    window.history.replaceState({}, '', url.toString());
  }

  render(announce = false) {
    const v = this.variant;
    const strings = this.data.strings;
    const qty = Math.max(1, Number(this.qtyInput?.value) || 1);

    this.variantInput.value = v ? v.id : '';

    // Selling plan allocation for this variant (may be missing).
    const alloc = v && this.lastPlanId ? v.plans[this.lastPlanId] : null;
    const subscribing = Boolean(this.planId && v && v.plans[this.planId]);
    if (this.planInput) {
      this.planInput.value = subscribing ? this.planId : '';
      this.planInput.disabled = !subscribing;
    }
    const unit = v ? (subscribing ? v.plans[this.planId].price : v.price) : 0;

    // Card prices + tag.
    this.querySelectorAll('[data-sub-price]').forEach((el) => { el.textContent = alloc ? money(alloc.price) : ''; });
    const was = this.querySelector('[data-sub-was]');
    if (was) {
      const show = alloc && alloc.compareAt > alloc.price;
      was.hidden = !show;
      const value = was.querySelector('[data-sub-was-value]');
      if (value && alloc) value.textContent = money(alloc.compareAt);
    }
    this.querySelectorAll('[data-onetime-price]').forEach((el) => { if (v) el.textContent = money(v.price); });
    const tag = this.querySelector('[data-save-tag]');
    if (tag) {
      let pct = this.data.planPercent[this.lastPlanId];
      if (pct == null && alloc && v.price > 0) pct = Math.floor(((v.price - alloc.price) / v.price) * 100);
      tag.hidden = !(pct > 0);
      if (pct > 0) tag.textContent = strings.savePercent.replace('__P__', Math.round(pct));
    }
    const subCard = this.querySelector('.pdp-card--subscribe');
    if (subCard) {
      subCard.toggleAttribute('data-unavailable', !alloc);
      const subRadio = subCard.querySelector('[data-purchase-radio]');
      if (subRadio) subRadio.disabled = !alloc;
      if (!alloc && subRadio?.checked) {
        const oneTime = this.querySelector('[data-purchase-radio][value="onetime"]');
        if (oneTime) oneTime.checked = true;
        this.planId = null;
      }
    }

    // Dynamic checkout can't take a subscription; hide it while subscribing.
    const dyn = this.querySelector('[data-dynamic-checkout]');
    if (dyn) dyn.hidden = subscribing;

    // Quantity limit from tracked inventory.
    if (this.qtyInput) {
      if (v?.max) this.qtyInput.max = v.max; else this.qtyInput.removeAttribute('max');
      if (v?.max && Number(this.qtyInput.value) > v.max) this.qtyInput.value = v.max;
      if (Number(this.qtyInput.value) < 1 || Number.isNaN(Number(this.qtyInput.value))) this.qtyInput.value = 1;
      this.qtyInput.closest('quantity-input')?.update?.();
    }

    // Main button + sticky bar.
    const available = Boolean(v && v.available);
    const label = !v ? strings.unavailable : available ? `${this.data.buttonLabel} — ${money(unit * qty)}` : strings.soldOut;
    [this.submit, this.stickyBtn].forEach((b) => {
      if (!b || b.getAttribute('aria-busy') === 'true') return;
      b.disabled = !available;
      if (available) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
    });
    const main = this.submit?.querySelector('[data-submit-label]');
    if (main) main.textContent = label;
    const stickyLabel = this.querySelector('[data-sticky-label]');
    if (stickyLabel) stickyLabel.textContent = available ? this.data.buttonLabel : (v ? strings.soldOut : strings.unavailable);
    const meta = this.querySelector('[data-sticky-meta]');
    if (meta && v) meta.textContent = subscribing ? strings.stickySubscribe.replace('__PRICE__', money(unit)) : money(unit);

    if (announce) {
      const live = this.querySelector('[data-live]');
      if (live) live.textContent = label;
    }
  }

  initSticky() {
    if (!this.sticky || !this.submit || !('IntersectionObserver' in window)) return;
    let buttonGone = false;
    let footerVisible = false;
    const apply = () => {
      const show = buttonGone && !footerVisible;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.inert = !show;
    };
    new IntersectionObserver(([entry]) => {
      // Only once the button has scrolled up past the top, not before reaching it.
      buttonGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      apply();
    }).observe(this.submit);
    const footer = document.querySelector('.footer__legal') || document.querySelector('footer');
    if (footer) {
      new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; apply(); }).observe(footer);
    }
    // Backup for browsers that delay observer callbacks: re-check on scroll (once per frame at most).
    let queued = false;
    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        buttonGone = this.submit.getBoundingClientRect().bottom < 0;
        const f = footer?.getBoundingClientRect();
        footerVisible = Boolean(f && f.top < window.innerHeight && f.bottom > 0);
        apply();
      }, 100);
    }, { passive: true });
  }
}
customElements.define('pdp-main', PdpMain);
